import React, { useRef, useState } from 'react';
import axios from 'axios';
import { IoLocation } from 'react-icons/io5';
import { BiSolidPhoneCall } from 'react-icons/bi';
import { IoMdMail } from "react-icons/io";
import toast from "react-hot-toast";

const Contact = () => {
  const initialState = {
    name: "",
    phone: "",
    email: "",
    message: "",
    service: "",
    nameErr: "",
    phoneErr: "",
    emailErr: "",
    serviceErr: "",
    messageErr: ""
  };

  const [state, setState] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const form = useRef();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prevState) => ({ ...prevState, [name]: value, [`${name}Err`]: "" }));
  };

  const clearState = () => setState({ ...initialState });

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const { name, phone, email, service, message } = state;
    let valid = true;

    // === Validation ===
    if (!name) {
      setState((prev) => ({ ...prev, nameErr: "Name is required" }));
      valid = false;
    }
    if (!phone || !/^[0-9]{10}$/.test(phone)) {
      setState((prev) => ({ ...prev, phoneErr: "Please enter a valid 10-digit phone number" }));
      valid = false;
    }
    if (!email) {
      setState((prev) => ({ ...prev, emailErr: "Email is required" }));
      valid = false;
    }
    if (!service) {
      setState((prev) => ({ ...prev, serviceErr: "Service is required" }));
      valid = false;
    }
    if (!message) {
      setState((prev) => ({ ...prev, messageErr: "Message is required" }));
      valid = false;
    }

    if (!valid) return;

    try {
      setLoading(true);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/inquiry/create-inquiry`,
        { name, email, phone, service, message },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success("Message sent successfully!");
        clearState();
      }
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      toast.error("Failed to send message. Please try again!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-100 py-10 px-4 md:px-10">
      <div className="container mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-4xl font-bold text-gray-800 mb-2">Contact Us</h2>
          <p className="text-gray-600">
            We'd love to hear from you. Please fill out the form below or reach out to us directly.
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          {/* === Contact Form === */}
          <div className="w-full md:w-1/2 p-4 bg-white shadow-lg rounded-lg">
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Get in Touch</h3>
            <form ref={form} onSubmit={handleSendMessage} className="space-y-4">
              <div>
                <label className="block text-gray-700">Name</label>
                <input
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  placeholder="Enter your name"
                  name="name"
                  value={state.name}
                  onChange={handleChange}
                />
                {state.nameErr && <span className="text-red-400 text-xs">{state.nameErr}</span>}
              </div>

              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-gray-700">Email</label>
                  <input
                    type="email"
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                    placeholder="Enter your email"
                    name="email"
                    value={state.email}
                    onChange={handleChange}
                  />
                  {state.emailErr && <span className="text-red-400 text-xs">{state.emailErr}</span>}
                </div>

                <div className="flex-1">
                  <label className="block text-gray-700">Phone No</label>
                  <input
                    type="tel"
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                    placeholder="Enter your phone number"
                    name="phone"
                    value={state.phone}
                    onChange={handleChange}
                  />
                  {state.phoneErr && <span className="text-red-400 text-xs">{state.phoneErr}</span>}
                </div>
              </div>

              <div>
                <label className="block text-gray-700">Service</label>
                <input
                  type="text"
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  placeholder="Enter your service"
                  name="service"
                  value={state.service}
                  onChange={handleChange}
                />
                {state.serviceErr && <span className="text-red-400 text-xs">{state.serviceErr}</span>}
              </div>

              <div>
                <label className="block text-gray-700">Message</label>
                <textarea
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  placeholder="Enter your message"
                  rows="4"
                  name="message"
                  value={state.message}
                  onChange={handleChange}
                />
                {state.messageErr && <span className="text-red-400 text-xs">{state.messageErr}</span>}
              </div>

              <div>
                <button
                  disabled={loading}
                  type="submit"
                  className="w-full bg-[#FFAC26] text-white py-2 rounded-md hover:bg-[#f08f4e]"
                >
                  {loading ? "Sending..." : "SUBMIT"}
                </button>
              </div>
            </form>
          </div>

          {/* === Contact Details === */}
          <div className="w-full md:w-1/2 p-4 bg-white shadow-lg rounded-lg">
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Contact Details</h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <BiSolidPhoneCall className="text-gray-700" size={24} />
                <span className="ml-2 text-gray-700">+91-7809477683</span>
              </div>
              <div className="flex items-center">
                <IoMdMail className="text-gray-700" size={24} />
                <span className="ml-2 text-gray-700">bbsrservices@gmail.com</span>
              </div>
              <div className="flex items-center">
                <IoLocation className="text-gray-700 h-14 w-14" />
                <span className="ml-2 text-gray-700">
                  Plot No-587/588, Antaryami Niwas, Ground Floor Mahaveer Nagar,
                  Garage Chhak, Lewis Rd, Samantarapur, Bhubaneswar, Odisha 751002
                </span>
              </div>
               <div className="flex items-center">
                <IoLocation className="text-gray-700 h-14 w-14" />
                <span className="ml-2 text-gray-700">
                  ADDRESS:1467 ,New Forest Park,Bhubaneswar ,Odisha,Pin: 751009,MAIL US:bbsrservices@gmail.com, website:www.bbsrservices.com .
                </span>
              </div>
              <div className="mt-4">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12995.751724838696!2d85.84335257007044!3d20.2374223912219!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a19a789b5f0bfb5%3A0xfcf892c80c3845ec!2sBBSR%20SERVICES!5e0!3m2!1sen!2sin!4v1759821024459!5m2!1sen!2sin"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
